import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Rocket,
  Code,
  Psychology,
  TrendingUp,
  CheckCircle,
  School,
  WorkspacePremium,
  Groups,
  EmojiEvents,
  ContactSupport,
} from '@mui/icons-material';

const Training = () => {
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    interests: ''
  });

  /* ===== PROGRAMS (JSR PRIORITY ORDER) ===== */
  const trainingPrograms = [
    {
      type: 'career',
      title: 'Career Acceleration',
      subtitle: 'Academic & Professional Growth',
      icon: TrendingUp,
      level: 'All Levels',
      duration: 'Flexible',
      mode: 'Mentorship',
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800, #E65100)',
      features: [
        'Career Guidance',
        'Academic Re-entry Support',
        'Interview Preparation',
        'Resume & Portfolio Building',
        'Placement Assistance'
      ]
    },
    {
      type: 'development',
      title: 'Development Bootcamps',
      subtitle: 'Software & Full-Stack Training',
      icon: Code,
      level: 'Intermediate',
      duration: '12 Weeks',
      mode: 'Hybrid',
      color: '#2196F3',
      gradient: 'linear-gradient(135deg, #2196F3, #0D47A1)',
      features: [
        'MERN / MEAN Stack',
        'API Development',
        'Database Management',
        'Cloud Deployment',
        'Real Projects'
      ]
    },
    {
      type: 'ai-ml',
      title: 'AI & ML Training',
      subtitle: 'Advanced Technology Programs',
      icon: Psychology,
      level: 'Beginner to Advanced',
      duration: '8 Weeks',
      mode: 'Online',
      color: '#ff4d4d',
      gradient: 'linear-gradient(135deg, #ff4d4d, #8b0000)',
      features: [
        'Machine Learning Basics',
        'Deep Learning',
        'NLP & Computer Vision',
        'Industry Use Cases',
        'Certification Guidance'
      ]
    }
  ];

  const stats = [
    { icon: School, value: '10K+', label: 'Students Guided' },
    { icon: WorkspacePremium, value: '92%', label: 'Success Rate' },
    { icon: Groups, value: '50+', label: 'Expert Mentors' },
    { icon: EmojiEvents, value: '200+', label: 'Projects' },
  ];

  const handleEnrollClick = (program) => {
    setSelectedCourse(program);
    setFormData(prev => ({ ...prev, course: program.title }));
    setEnrollDialogOpen(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setEnrollDialogOpen(false);
    setFormData({ name: '', email: '', phone: '', course: '', interests: '' });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1128, #1a2342)',
        py: 8
      }}
    >
      <Container maxWidth="xl">

        {/* ===== HERO ===== */}
        <Box textAlign="center" mb={6}>
          <Chip
            label="Professional & Academic Training"
            sx={{
              background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
              color: 'white',
              fontWeight: 700,
              mb: 2
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              background: 'linear-gradient(45deg, #ffffff, #ff4d4d)',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            Career & Academic Advancement
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
            Academic guidance, skill development & career transformation
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: "0.85rem",
              color: "rgba(255,255,255,0.65)",
              fontStyle: "italic",
              textAlign: "center",
              letterSpacing: "0.04em"
            }}
          >
            “கேடில் விழுச்செல்வம் கல்வி ஒருவற்கு
            மாடல்ல மற்றை யவை”
          </Typography>

          <Typography
            sx={{
              mt: 0.4,
              fontSize: "0.75rem",
              color: "rgba(255,255,255,0.55)",
              textAlign: "center"
            }}
          >
            “Education is the imperishable wealth; all other wealth fades.”
          </Typography>

        </Box>

        {/* ===== ACADEMIC PARTNER ===== */}
        <Box
          sx={{
            mb: 6,
            p: 4,
            borderRadius: 4,
            border: '2px solid rgba(139,0,0,0.4)',
            background: 'rgba(255,255,255,0.05)'
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Typography variant="h4" fontWeight={800} color="white" mb={2}>
                Academic Partner – JSR Academy
              </Typography>

              <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 2 }}>
                Our academic and skill development initiatives are supported
                through collaboration with <strong>JSR Academy, Mappadugai</strong>
                (KBGJSR Academy Pvt. Ltd.), Mayiladuthurai, Tamil Nadu.
              </Typography>

              <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
                JSR Academy is affiliated with <strong>ASCODET</strong> and
                associated with universities such as
                <strong> Kalasalingam Academy of Research and Education</strong> and
                <strong> Sikkim Skill University</strong>.
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              {[
                'Open Schooling (10th & 12th)',
                'UG / PG Degree Assistance',
                'M.Phil & Ph.D Guidance',
                'Skill & Software Training',
                'Career Recovery Programs'
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', mb: 1 }}>
                  <CheckCircle sx={{ color: '#ff4d4d', mr: 1 }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
                    {item}
                  </Typography>
                </Box>
              ))}

              <Box mt={3} pt={2} borderTop="1px solid rgba(255,255,255,0.2)">
                <Typography color="white" fontWeight={700}>
                  JSR Academy Contact
                </Typography>
                <Typography color="#ff4d4d" fontWeight={700}>
                  📞 +91 94885 88105
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ===== PROGRAMS ===== */}
        <Box mb={6}>
          <Typography variant="h3" align="center" fontWeight={800} color="#ff4d4d">
            Our Programs
          </Typography>
          <Typography align="center" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
            Academic guidance & skill-based training
          </Typography>

          <Grid container spacing={3}>
            {trainingPrograms.map((program, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card
                  sx={{
                    p: 2.5,
                    height: '100%',
                    borderRadius: 3,
                    border: `1.5px solid ${program.color}55`,
                    background: 'rgba(0,0,0,0.35)'
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box textAlign="center" mb={2}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          mx: 'auto',
                          mb: 1,
                          background: program.gradient,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <program.icon sx={{ color: 'white' }} />
                      </Box>
                      <Typography fontWeight={700} color="white">
                        {program.title}
                      </Typography>
                      <Typography fontSize="0.9rem" color={program.color}>
                        {program.subtitle}
                      </Typography>
                    </Box>

                    {program.features.map((f, idx) => (
                      <Box key={idx} sx={{ display: 'flex', mb: 1 }}>
                        <CheckCircle sx={{ fontSize: 16, color: program.color, mr: 1 }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                          {f}
                        </Typography>
                      </Box>
                    ))}

                    <Button
                      fullWidth
                      sx={{
                        mt: 2,
                        background: program.gradient,
                        color: 'white',
                        fontWeight: 700
                      }}
                      onClick={() => handleEnrollClick(program)}
                    >
                      Enquire Now
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ===== STATS ===== */}
        <Grid container spacing={3}>
          {stats.map((s, i) => (
            <Grid item xs={6} md={3} key={i} textAlign="center">
              <s.icon sx={{ fontSize: 36, color: '#ff4d4d' }} />
              <Typography color="white" fontWeight={800}>{s.value}</Typography>
              <Typography color="rgba(255,255,255,0.7)">{s.label}</Typography>
            </Grid>
          ))}
        </Grid>

      </Container>

      {/* ===== ENROLL DIALOG ===== */}
      <Dialog open={enrollDialogOpen} onClose={() => setEnrollDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle align="center">
          <Typography variant="h5" fontWeight={800} color="#ff4d4d">
            Enrollment Enquiry
          </Typography>
          <Typography>{selectedCourse?.title}</Typography>
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" sx={{ mb: 2 }} />
          <TextField fullWidth label="Email" sx={{ mb: 2 }} />
          <TextField fullWidth label="Phone" sx={{ mb: 2 }} />
          <TextField fullWidth label="Interests" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSubmit}>Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Training;
